/**
 * @fileoverview App Review Modal
 * 
 * Star rating modal that prompts engaged users to rate WNTD.
 * - 4-5 stars: Thanks + directs to App Store review
 * - 1-3 stars: Routes to feedback form
 * - Dismiss: Respects cooldown before re-asking
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useNavigate } from 'react-router-dom';

interface AppReviewModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  onDismiss: () => void;
  onSubmit: (args: { rating: number; directedToStore?: boolean; directedToFeedback?: boolean }) => void;
}

type Phase = 'rating' | 'positive' | 'negative';

const APP_STORE_REVIEW_URL = 'https://apps.apple.com/app/id6739854428?action=write-review';

export function AppReviewModal({ open, title, subtitle, onDismiss, onSubmit }: AppReviewModalProps) {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [phase, setPhase] = useState<Phase>('rating');
  const navigate = useNavigate();

  if (!open) return null;

  const handleStarClick = (star: number) => {
    setSelectedRating(star);
    if (star >= 4) {
      setPhase('positive');
      onSubmit({ rating: star, directedToStore: true });
    } else {
      setPhase('negative');
      onSubmit({ rating: star, directedToFeedback: true });
    }
  };

  const handleGoToAppStore = async () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      await Browser.open({ url: APP_STORE_REVIEW_URL });
    }
    onDismiss();
  };

  const handleGoToFeedback = () => {
    onDismiss();
    navigate('/settings', { state: { openFeedback: true } });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            onClick={phase === 'rating' ? onDismiss : undefined}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-card rounded-3xl p-6 shadow-2xl border border-border"
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 100, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Close button */}
            {phase === 'rating' && (
              <button
                onClick={onDismiss}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}

            <AnimatePresence mode="wait">
              {phase === 'rating' && (
                <motion.div
                  key="rating"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center text-center gap-4"
                >
                  <div className="text-3xl">✨</div>
                  <h2 className="text-xl font-bold text-foreground">{title}</h2>
                  <p className="text-sm text-muted-foreground">{subtitle}</p>

                  {/* Star rating */}
                  <div className="flex gap-2 py-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <motion.button
                        key={star}
                        onMouseEnter={() => setHoveredStar(star)}
                        onMouseLeave={() => setHoveredStar(0)}
                        onClick={() => handleStarClick(star)}
                        whileTap={{ scale: 1.3 }}
                        className="p-1 transition-colors"
                      >
                        <Star
                          className={`h-9 w-9 transition-all duration-150 ${
                            star <= (hoveredStar || selectedRating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-muted-foreground/30'
                          }`}
                        />
                      </motion.button>
                    ))}
                  </div>

                  <button
                    onClick={onDismiss}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
                  >
                    Maybe Later
                  </button>
                </motion.div>
              )}

              {phase === 'positive' && (
                <motion.div
                  key="positive"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center text-center gap-4"
                >
                  <div className="text-4xl">🎉</div>
                  <h2 className="text-xl font-bold text-foreground">Thank you!</h2>
                  <p className="text-sm text-muted-foreground">
                    We're so glad you're enjoying WNTD. Would you mind leaving a quick review on the App Store? It really helps!
                  </p>
                  <div className="flex flex-col gap-2 w-full mt-2">
                    <Button onClick={handleGoToAppStore} className="w-full rounded-2xl">
                      Rate on App Store
                    </Button>
                    <button
                      onClick={onDismiss}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Not now
                    </button>
                  </div>
                </motion.div>
              )}

              {phase === 'negative' && (
                <motion.div
                  key="negative"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col items-center text-center gap-4"
                >
                  <div className="text-4xl">💬</div>
                  <h2 className="text-xl font-bold text-foreground">We'd love to hear from you</h2>
                  <p className="text-sm text-muted-foreground">
                    Tell us how we can improve WNTD. Your feedback directly shapes what we build next.
                  </p>
                  <div className="flex flex-col gap-2 w-full mt-2">
                    <Button onClick={handleGoToFeedback} className="w-full rounded-2xl">
                      Share Feedback
                    </Button>
                    <button
                      onClick={onDismiss}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Not now
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
